import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	input,
	OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map, Observable, switchMap } from 'rxjs';

import {
	AvailabilityWidgetComponent,
	WidgetDataActionInterface,
} from '@valk-nx/compositions/ui-availability-widget/src/lib/availability-widget.component';
import { TranslatedSlug } from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import {
	fallbackLanguage,
	HOTEL_SLUG,
	LANGUAGE,
	Language,
} from '@valk-nx/core/lib/core';
import { ContentHotelInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import { CarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';
import { Carousel } from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

@Component({
	selector: 'sb-carousel-with-availability-widget',
	templateUrl: './carousel-with-availability-widget.html',
	imports: [
		AsyncPipe,
		CarouselComponent,
		AvailabilityWidgetComponent,
		StoryblokDirective,
	],
})
export class CarouselWithAvailabilityWidgetComponent
	extends StoryblokRootDirective
	implements OnInit
{
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly tagManagerFacade = inject(TagManagerFacade);
	private readonly cd = inject(ChangeDetectorRef);
	readonly hotelSlug = inject(HOTEL_SLUG);
	readonly bookingtool = inject(BOOKINGTOOL);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	@Input() blocks: Carousel[] = [];
	@Input({ required: true }) hotelGUID!: string;
	openInNewTab = input<boolean>(false);

	carousel!: Carousel;
	hotel$: Observable<ContentHotelInterface>;
	translatedSlugs$: Observable<TranslatedSlug[]>;
	language: string = fallbackLanguage;

	constructor() {
		super();
		this.language = this.rootLanguage;
		this.translatedSlugs$ =
			this.sbService.translatedSlugs$.pipe(takeUntilDestroyed());
	}

	ngOnInit() {
		this.hotel$ = this.translatedSlugs$.pipe(
			switchMap((translatedSlug) => {
				this.language = (
					translatedSlug.find((x) => x.selected) ?? translatedSlug[0]
				).lang.toLowerCase();
				this.cd.detectChanges();
				return this.contentService
					.getHotelInfo(this.language)
					.pipe(map((response) => response.data));
			}),
		);

		this.carousel = this.blocks.find((x) => x.component === 'carousel');
	}

	widgetCTA(eventData: {
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}) {
		this.tagManagerFacade.availabilityWidget({
			...eventData,
			data: { ...eventData.data, hotelSlug: this.hotelSlug },
		});
	}
}
